import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { CourseWithoutBlocks, CourseUpdate } from '@/types/course'
import { cn } from '@/lib/utils'
import { useUpdateCourseMutation } from '@/features/editor/coursesApi'
import { useAppDispatch } from '@/store/hooks'
import { setSaveStatus, type ContentLang } from '@/features/editor/editorSlice'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { useSaveAfterPause } from '@/features/editor/hooks/useSaveAfterPause'
import { getLocalizedField, setLocalizedField } from '@/features/editor/lib/localizedField'
import { useIsCourseAuthor } from '@/features/editor/hooks/useIsCourseAuthor'
import { REVIEW_LOCK_TEXT } from '@/features/editor/lib/reviewLockText'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { useIsCourseTextLocked } from '../studio/useBlocksInRun'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from './messages'

const titleLockReason = (
  isAuthor: boolean,
  isTextLocked: boolean,
  isFrozen: boolean,
): string | undefined => {
  const m = messagesNow(messages)
  if (!isAuthor) return m.noRights
  if (isFrozen) return REVIEW_LOCK_TEXT
  return isTextLocked ? m.titleTranslating : undefined
}

interface Props {
  course: CourseWithoutBlocks
}

export const TitleInput = ({ course }: Props) => {
  const dispatch = useAppDispatch()
  const m = useMessages(messages)
  const contentLang = useContentLang()
  const [updateCourse] = useUpdateCourseMutation()
  const isCourseAuthor = useIsCourseAuthor(course)
  // Тот же текст курса, что «Название курса» в настройках, — и тот же гейт:
  // перевод переписывает его по снимку со старта, проверка отбивает запись.
  const isTextLocked = useIsCourseTextLocked(course.id)
  const isFrozen = useIsCourseFrozen(course.id)

  const currentTitle = getLocalizedField(course, 'name', contentLang) ?? ''
  const [title, setTitle] = useState(currentTitle)
  // Пока поле в фокусе, ответ сервера текст не трогает: он пришёл на прошлую паузу и
  // затёр бы буквы, набранные после неё.
  const focusedRef = useRef(false)
  useEffect(() => {
    if (!focusedRef.current) setTitle(currentTitle)
  }, [currentTitle])

  // Что уже ушло на сервер, и на каком языке: список курса приезжает позже ответа, и
  // без этого выход из поля сразу после паузы отправил бы то же значение второй раз.
  // Дубль — только то же значение на том же языке; как только сервер догнал или язык
  // сменился, запись забывается, иначе возврат к прежнему тексту молча не уходил бы.
  const sentRef = useRef<{ lang: ContentLang; value: string } | null>(null)
  useEffect(() => {
    const sent = sentRef.current
    if (sent && (sent.lang !== contentLang || sent.value === currentTitle)) sentRef.current = null
  }, [currentTitle, contentLang])
  const saveTitle = async () => {
    const sent = sentRef.current
    if (title === currentTitle || (sent?.lang === contentLang && sent.value === title)) return
    sentRef.current = { lang: contentLang, value: title }
    dispatch(setSaveStatus('saving'))
    try {
      await updateCourse({
        id: course.id,
        patch: setLocalizedField('name', contentLang, title) as CourseUpdate,
      }).unwrap()
      dispatch(setSaveStatus('saved'))
    } catch {
      sentRef.current = null
      dispatch(setSaveStatus('error'))
      toast.error(m.titleSaveFailed)
    }
  }

  // Пустое название — промежуточное состояние набора, оно уходит только при выходе из поля.
  useSaveAfterPause(title, saveTitle, title.trim() !== '' && isCourseAuthor && !isTextLocked)

  // Общие для поля и его невидимого «двойника»: ширину задаёт двойник с тем же
  // текстом, шрифтом и отступами — поле ровно по тексту, без лишнего хвоста.
  const box = 'col-start-1 row-start-1 px-2 py-1 text-sm font-medium border'
  return (
    // В покое не шире 280px (длинное — троеточием); при наведении и в фокусе
    // раздвигается до полного названия, насколько позволяет шапка.
    // `minmax(0,1fr)`: без него колонка грида растягивалась по невидимому
    // двойнику, и ограничение ширины не работало — название шло целиком.
    <span className="inline-grid min-w-0 max-w-[280px] grid-cols-[minmax(0,1fr)] transition-[max-width] duration-300 focus-within:max-w-[70vw] hover:max-w-[70vw]">
      <span
        aria-hidden="true"
        className={cn(box, 'invisible min-w-0 overflow-hidden whitespace-pre border-transparent')}
      >
        {title || m.untitled}
      </span>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onFocus={() => {
          focusedRef.current = true
        }}
        onBlur={() => {
          focusedRef.current = false
          void saveTitle()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        disabled={!isCourseAuthor || isTextLocked}
        aria-label={m.a11yCourseTitle}
        // Подсказка — только когда поле закрыто и надо объяснить почему;
        // «Название курса» над названием курса ничего не сообщало.
        title={titleLockReason(isCourseAuthor, isTextLocked, isFrozen)}
        className={cn(
          box,
          'w-full min-w-0 truncate rounded-md bg-transparent text-foreground',
          'border-transparent outline-none',
          'hover:border-border focus:border-border focus:ring-0',
          'placeholder:text-muted-foreground',
        )}
        placeholder={m.untitled}
      />
    </span>
  )
}
